/**
 * Frontend API client for Klaviyo integration management.
 */
import { apiFetch } from './api';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface KlaviyoStatus {
  connected: boolean;
  label?: string;
  key_prefix?: string;
  created_at?: number;
  last_used_at?: number;
}

export interface KlaviyoMapping {
  klaviyo_template_id: string;
  template_name: string;
  last_pushed_version: number;
  last_pushed_at: number;
}

export interface PushResult {
  ok: true;
  template_id: string;
  template_name: string;
  action: 'created' | 'updated';
  previous_version?: number;
}

// ---------------------------------------------------------------------------
// API calls
// ---------------------------------------------------------------------------

export function getKlaviyoStatus(): Promise<KlaviyoStatus> {
  return apiFetch<KlaviyoStatus>('/api/integrations/klaviyo/status');
}

export function connectKlaviyo(apiKey: string, label?: string): Promise<{ ok: true; key_prefix: string }> {
  return apiFetch('/api/integrations/klaviyo/connect', {
    method: 'POST',
    json: { api_key: apiKey, label: label || undefined },
  });
}

export function testKlaviyoConnection(): Promise<{ ok: boolean; message: string }> {
  return apiFetch('/api/integrations/klaviyo/test', { method: 'POST' });
}

export function disconnectKlaviyo(): Promise<{ ok: true }> {
  return apiFetch('/api/integrations/klaviyo/disconnect', { method: 'DELETE' });
}

export function getKlaviyoMapping(draftId: string): Promise<{ mapping: KlaviyoMapping | null }> {
  return apiFetch(`/api/integrations/klaviyo/mapping/${draftId}`);
}

export function pushToKlaviyo(
  draftId: string,
  templateName: string,
  opts?: { version?: number; forceNew?: boolean; inlinedHtml?: string },
): Promise<PushResult> {
  return apiFetch('/api/integrations/klaviyo/push-template', {
    method: 'POST',
    json: {
      draft_id: draftId,
      template_name: templateName,
      version: opts?.version,
      force_new: opts?.forceNew ?? false,
      html: opts?.inlinedHtml,
    },
  });
}
